/**
 * IdentityComplianceEvidencePageV2 — Stage 4-5.
 *
 * Auditor-facing view of compliance binding coverage. For each
 * framework binding active in the org, shows which agents are covered
 * and where the binding came from (Org default vs Tenant addition).
 *
 * Reachable from the Identity landing page operational-views shelf.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, AlertTriangle, FileCheck2, Users } from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { getIdentityComplianceEvidence } from '../services/api';

export default function IdentityComplianceEvidencePageV2() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    getIdentityComplianceEvidence().then((res) => {
      if (res?.ok === false) setError(res.error || 'Could not load compliance evidence.');
      else setData(res?.data || null);
      setLoading(false);
    });
  }, []);

  const toggle = (binding) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(binding)) next.delete(binding);
      else next.add(binding);
      return next;
    });
  };

  return (
    <V2PageShell
      title="Compliance evidence"
      subtitle="Per-framework binding coverage for the agents in this organization."
      backTo="/identity"
      backLabel="Back to Identity"
      tags={
        <>
          <Pill tone="trust"  size="sm">Identity</Pill>
          <Pill tone="action" size="sm" dot>Audit</Pill>
        </>
      }
    >
      {loading && (
        <div className="flex flex-col gap-2">
          <Skeleton height={56} /><Skeleton height={56} /><Skeleton height={56} />
        </div>
      )}

      {!loading && error && (
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title="Could not load compliance evidence"
            description={error}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && data && (!data.bindings || data.bindings.length === 0) && (
        <Card>
          <EmptyState
            icon={<FileCheck2 />}
            title="No compliance bindings configured"
            description="Configure compliance bindings on the Organization page or via tenant overrides to see coverage here."
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => navigate('/identity/organization')}>
                Configure Org bindings
              </Button>
            }
          />
        </Card>
      )}

      {!loading && !error && data && data.bindings && data.bindings.length > 0 && (
        <>
          <Card padding="sm" className="mb-3">
            <div className="flex items-center gap-4 text-[12.5px]">
              <span className="text-v2-ink-secondary">
                <strong className="text-v2-ink">{data.totalAgents}</strong> agents in org
              </span>
              <span className="text-v2-ink-secondary">
                <strong className="text-v2-ink">{data.bindings.length}</strong> active bindings
              </span>
              <span className="text-v2-ink-secondary">
                Org bindings:&nbsp;
                <strong className="text-v2-ink">{(data.orgBindings || []).length}</strong>
              </span>
            </div>
          </Card>

          <div className="flex flex-col gap-2">
            {data.bindings.map((b) => {
              const isExpanded = expanded.has(b.binding);
              const tone = b.source === 'Organization' ? 'action' : 'trust';
              return (
                <Card key={b.binding} padding="md">
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => toggle(b.binding)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(b.binding); } }}>
                    <Scale size={16} className="text-v2-action" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-v2 text-[13px] font-semibold text-v2-ink">
                        {b.binding}
                      </div>
                      <div className="text-[11px] text-v2-ink-muted mt-0.5">
                        Source: <span className="text-v2-ink-secondary">{b.source}</span>
                      </div>
                    </div>
                    <Pill tone={tone} size="sm">{b.source}</Pill>
                    <Pill tone="muted" size="sm">
                      <Users size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
                      {b.agentCount} agents
                    </Pill>
                  </div>
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-v2-border-subtle">
                      {b.agents && b.agents.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {b.agents.map((a) => (
                            <div key={a.id}
                                 className="flex items-center gap-3 px-3 py-1.5 hover:bg-v2-muted/50 rounded-v2-sm cursor-pointer"
                                 onClick={() => navigate(`/identity/agents/${a.id}`)}>
                              <span className="text-[13px] font-semibold text-v2-ink min-w-[160px] truncate">
                                {a.name || a.id}
                              </span>
                              {a.trustBand && (
                                <Pill tone="muted" size="sm">{a.trustBand}</Pill>
                              )}
                              <span className="text-[11px] text-v2-ink-muted">→ View detail</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-[12px] text-v2-ink-muted italic">
                          No agents currently covered. Binding is configured but no agents fall under its tag.
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}
    </V2PageShell>
  );
}
